/**
 * ChakraEventAPI - Unified chakra management system
 * Provides single API for all chakra types with event system and state persistence
 */

import { devWarn, devError } from '../../../../../utils/devLog';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../../utils/localStorageUtils';

import { globalEventStream, createEvent } from '../../universal_architecture/core/EventStream';
import { JatakaResponse } from '../../../../../models/api';

export interface ChakraEventConfig {
    id: string;
    type: 'nakshatra' | 'muhurta' | 'ashtamsha' | 'custom';
    label: string;
    storageKey: string;
    defaultValue: boolean;
    validation?: (apiResponse: JatakaResponse) => boolean;
}

export interface ChakraEventData {
    enabled: boolean;
    chartId: string | null;
    timestamp: number;
}

export interface ChakraSettings {
    isVisible: boolean;
    isLoading: boolean;
    error: string | null;
    toggle: (enabled: boolean) => Promise<void>;
    config: ChakraEventConfig | null;
}

/**
 * Main ChakraEventAPI class - singleton for managing all chakra interactions
 */
class ChakraEventAPI {
    private configs = new Map<string, ChakraEventConfig>();
    private listeners = new Map<string, Set<(data: ChakraEventData) => void>>();

    /**
     * Register a chakra configuration
     */
    registerChakra(config: ChakraEventConfig): void {
        this.configs.set(config.id, config);
    }

    /**
     * Get all registered chakras of a specific type
     */
    getChakrasByType(type: ChakraEventConfig['type']): ChakraEventConfig[] {
        return Array.from(this.configs.values()).filter(config => config.type === type);
    }


    /**
     * Event subscription system
     */
    onChakraToggle(chakraId: string, callback: (data: ChakraEventData) => void): () => void {
        if (!this.listeners.has(chakraId)) {
            this.listeners.set(chakraId, new Set());
        }

        this.listeners.get(chakraId)!.add(callback);

        // Return unsubscribe function
        return () => {
            this.listeners.get(chakraId)?.delete(callback);
        };
    }

    /**
     * Emit events for chakra state changes
     */
    private emitEvent(chakraId: string, data: ChakraEventData): void {
        // Local listeners
        const listeners = this.listeners.get(chakraId);
        listeners?.forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                devError(`❌ ChakraEventAPI: Listener error for ${chakraId}:`, error);
            }
        });

        // Global event stream
        globalEventStream.emit(createEvent(
            'chakra:toggled',
            'ChakraEventAPI',
            {
                chakraId,
                ...data
            }
        ));
    }

    /**
     * Load chakra state from storage
     */
    private loadChakraState(chakraId: string, chartId: string): boolean {
        const config = this.configs.get(chakraId);
        if (!config) return false;

        const key = `${config.storageKey}:${chartId}`;
        const stored = getLocalStorageItem<{ enabled?: boolean } | null>(key, null);
        return stored?.enabled ?? config.defaultValue;
    }

    /**
     * Save chakra state to storage
     */
    private saveChakraState(chakraId: string, chartId: string, enabled: boolean): void {
        const config = this.configs.get(chakraId);
        if (!config) throw new Error(`Chakra ${chakraId} not registered`);

        const key = `${config.storageKey}:${chartId}`;
        setLocalStorageItem(key, { enabled, lastUpdated: Date.now() });
    }

    /**
     * Validate chakra visibility based on API response
     */
    isChakraAvailable(chakraId: string, apiResponse: JatakaResponse): boolean {
        const config = this.configs.get(chakraId);
        if (!config) return false;

        return config.validation ? config.validation(apiResponse) : true;
    }

    /**
     * Get debug information about registered chakras
     */
    getDebugInfo(): Record<string, any> {
        return {
            registeredChakras: Array.from(this.configs.keys()),
            activeListeners: Object.fromEntries(
                Array.from(this.listeners.entries()).map(([id, listeners]) => [id, listeners.size])
            )
        };
    }
}

// Singleton instance
export const chakraEventAPI = new ChakraEventAPI();

// Auto-register existing chakras for backward compatibility
chakraEventAPI.registerChakra({
    id: 'nakshatra-chakra',
    type: 'nakshatra',
    label: 'Nakshatra Chakra',
    storageKey: 'nakshatra_chakra_enabled',
    defaultValue: false
});

chakraEventAPI.registerChakra({
    id: 'muhurta-chakra',
    type: 'muhurta',
    label: 'Muhurta Chakra',
    storageKey: 'muhurta_chakra_enabled',
    defaultValue: false
});

chakraEventAPI.registerChakra({
    id: 'ashtamsha-chakra',
    type: 'ashtamsha',
    label: 'Ashtamsha Chakra',
    storageKey: 'ashtamsha_chakra_enabled',
    defaultValue: false
});

export default chakraEventAPI;